import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextField, CircularProgress } from '@mui/material';
import { useUserStore } from '../../store/useUserStore';
import useUpdateUser from '../../hook/useUpdateUser';
import { useRegisterKidMutation } from '../../queries';
import { kidsApi } from '../../assets/api/apis';
import Url from '../../data/url';
import toast from 'react-hot-toast';
import useRandomProfile from '../../hook/useRandomProfile';
import { GENDER } from '../../data/define';
import FullHeightContainer from '../../component/common/FullHeightContainer';
import TopHeaderWithPrev from '../../component/common/TopHeaderWithPrev';
import BottomFixedBtn from '../../component/common/BottomFixedBtn';
import TwoBtns from '../../component/common/TwoBtns';
import BirthDatePickerDialog from '../../component/register/BirthDatePickerDialog';
import { Gender } from '../../types';
import { useTranslation } from '../../hook/useTranslation';

export default function RegisterKidsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { updateUser } = useUpdateUser();
  const { randomProfile } = useRandomProfile();
  const [loading, setLoading] = useState(false);
  const user = useUserStore();
  const [nameInput, setNameInput] = useState({ value: '', isDone: false });
  const [birthInput, setBirthInput] = useState<string | null>(null);
  const [genderInput, setGenderInput] = useState<string>(GENDER.UNKNOWN);
  const [focusField, setFocusField] = useState<string | null>(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const birthInputRef = useRef<HTMLInputElement>(null);

  const { mutateAsync: registerKid } = useRegisterKidMutation();

  // after login, register kid
  const handleRegisterKids = useCallback(async () => {
    try {
      setLoading(true);
      if (!user.id || !birthInput) return;
      const apiParams = {
        userId: user.id,
        kidName: nameInput.value,
        kidBirth: birthInput,
        gender: genderInput as Gender,
      };
      const response = await registerKid(apiParams);
      const status = response.status;
      const data = response.data;

      let profileResponse;
      if (status >= 200 && status < 300) {
        const sameGenderKidsCount =
          user.kids?.filter((kid) => kid.gender === genderInput).length || 0;
        const profileOrder = (sameGenderKidsCount % 3) + 1;
        const profile = randomProfile(genderInput as any, profileOrder);
        const res = await fetch(profile);
        const blob = await res.blob();
        const file = new File([blob], `draft_profile-${data.id}.png`, { type: blob.type });
        const fd = new FormData();
        fd.append('file', file);
        profileResponse = await kidsApi.editKidProfileImage(data.id, fd);
      }
      if (profileResponse?.status >= 200 && profileResponse?.status < 300) {
        updateUser();
        navigate(Url.Home);
      }
    } catch (error) {
      console.error('Error registering kid:', error);
      toast.error(t('welcome.toast_fail'));
    } finally {
      setLoading(false);
    }
  }, [
    user,
    nameInput,
    birthInput,
    genderInput,
    navigate,
    updateUser,
    randomProfile,
    registerKid,
    t,
  ]);

  // 이름 입력 완료 후 생년월일 필드에 자동 포커스 및 다이얼로그 열기
  useEffect(() => {
    if (nameInput.isDone && !birthInput && birthInputRef.current) {
      setTimeout(() => {
        birthInputRef.current?.focus();
        setDatePickerOpen(true);
      }, 100);
    }
  }, [nameInput.isDone, birthInput]);

  return (
    <FullHeightContainer>
      <TopHeaderWithPrev onClickPrev={() => navigate(-1)} />
      <div className="pageInner bg-[#FFF]">
        {loading && (
          <div className="absolute top-0 bottom-0 left-0 right-0 flex items-center justify-center ">
            <CircularProgress />
          </div>
        )}
        {!nameInput.isDone && !birthInput && (
          <div className="my-[30px] w-full text-[24px] font-extrabold">
            <div className="w-full text-left">
              {t('register.name_title')
                .split('\n')
                .map((line, i) => (
                  <span key={i}>
                    {line}
                    {i === 0 && <br />}
                  </span>
                ))}
            </div>
          </div>
        )}
        {nameInput.isDone && !birthInput && (
          <div className="my-[30px] w-full text-[24px] font-extrabold">
            <div className="w-full text-left">
              {t('register.birth_title')
                .split('\n')
                .map((line, i) => (
                  <span key={i}>
                    {line}
                    {i === 0 && <br />}
                  </span>
                ))}
            </div>
          </div>
        )}
        {nameInput.isDone && birthInput && (
          <div className="my-[30px] w-full text-[24px] font-extrabold">
            <div className="w-full text-left">
              {t('register.gender_title')
                .split('\n')
                .map((line, i) => (
                  <span key={i}>
                    {line}
                    {i === 0 && <br />}
                  </span>
                ))}
            </div>
          </div>
        )}
        {birthInput && (
          <div className="w-full flex flex-col items-start justify-start gap-2 mb-7">
            <div className="font-normal text-[14px] text-[#363636]">{t('register.gender')}</div>
            <TwoBtns
              leftTitle={t('register.male')}
              rightTitle={t('register.female')}
              onLeftClick={() => setGenderInput(GENDER.MALE)}
              onRightClick={() => setGenderInput(GENDER.FEMALE)}
              leftFocus={genderInput === GENDER.MALE}
              rightFocus={genderInput === GENDER.FEMALE}
            />
          </div>
        )}
        {nameInput.isDone && (
          <div className="w-full flex flex-col items-start justify-start gap-2 mb-7">
            <div
              className="font-normal text-[14px] text-[#363636]"
              style={{ color: focusField === 'birth' ? '#5EBEBB' : '#363636' }}
            >
              {t('register.birth')}
            </div>
            <TextField
              inputRef={birthInputRef}
              variant="standard"
              value={birthInput ?? ''}
              fullWidth
              placeholder="0000-00-00"
              onClick={() => setDatePickerOpen(true)}
              onFocus={() => {
                setFocusField('birth');
              }}
              onBlur={() => setFocusField(null)}
            />
            <BirthDatePickerDialog
              open={datePickerOpen}
              onClose={() => {
                setDatePickerOpen(false);
                setTimeout(() => {
                  birthInputRef.current?.blur();
                  setFocusField(null);
                }, 150);
              }}
              onConfirm={() => {
                setDatePickerOpen(false);
                setTimeout(() => {
                  birthInputRef.current?.blur();
                  setFocusField(null);
                }, 150);
              }}
              birthInput={birthInput || ''}
              setBirthInput={setBirthInput as any}
            />
          </div>
        )}
        <div className="w-full flex flex-col items-start justify-start gap-2">
          <div
            className="font-normal text-[14px] text-[#363636]"
            style={{ color: focusField === 'name' ? '#5EBEBB' : '#363636' }}
          >
            {t('register.name')}
          </div>
          <TextField
            variant="standard"
            value={nameInput.value}
            onChange={(e) => {
              const inputValue = e.target.value;
              setNameInput((prev) => ({ ...prev, value: inputValue }));
            }}
            fullWidth
            placeholder={t('register.name_placeholder')}
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                if (nameInput.value.length >= 1) {
                  setNameInput((prev) => ({ ...prev, isDone: true }));
                }
              }
            }}
            onFocus={() => setFocusField('name')}
            onBlur={() => {
              if (nameInput.value.length >= 1) {
                setNameInput((prev) => ({ ...prev, isDone: true }));
              } else {
                setFocusField(null);
              }
            }}
          />
        </div>
      </div>
      <BottomFixedBtn
        title={t('register.submit')}
        onClick={handleRegisterKids}
        disabled={
          nameInput.value.length < 1 ||
          (birthInput?.length ?? 0) < 8 ||
          genderInput === GENDER.UNKNOWN
        }
      />
    </FullHeightContainer>
  );
}
